import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchInvoiceDueInterest,
  AdminFetchInvoices,
  AdminRunInvoiceAction,
  AdminSetInvoiceActionProgress,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import {
  INVOICE_ACTION_ENDPOINTS,
  invoiceActionFallbackMessages,
} from 'src/constants/invoice/constants'
import type {
  FetchInvoiceDueInterestPayload,
  FetchInvoicesPayload,
  InvoiceActionResult,
  InvoiceDueInterest,
  InvoicesPage,
  RunInvoiceActionPayload,
} from 'src/interfaces/invoice'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'
import type { CallApiResponse } from './api/callApi'

/**
 * The sagas' own view of their payloads: `partnerId` is optional here because each handler
 * guards it at runtime — an action can be dispatched with a malformed payload from anywhere.
 * The exported types in `interfaces/invoice.ts` stay strict for the page that builds them.
 * Same split as `onFetchMarkets` (sagas/market.ts) and the gigsafe-config sagas.
 */
type FetchInvoicesActionPayload = Omit<FetchInvoicesPayload, 'partnerId'> & { partnerId?: string }

type FetchInvoiceDueInterestActionPayload = Omit<FetchInvoiceDueInterestPayload, 'partnerId'> & {
  partnerId?: string
}

type RunInvoiceActionActionPayload = Omit<RunInvoiceActionPayload, 'partnerId'> & {
  partnerId?: string
}

export function* onFetchInvoices(action: AsyncAction<FetchInvoicesActionPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchInvoices.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // This endpoint answers `{ data, pagination }`, NOT a raw array — `normalizeApiListPayload`
    // would discard the pagination the grid's `rowCount` depends on, so it is not used here.
    const { payload }: { payload: InvoicesPage } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInvoices,
      {
        method: 'GET',
        params: [{ field: ':partnerId', value: partnerId }],
        query: { limit: action.payload.limit, offset: action.payload.offset },
      },
    )

    yield put(AdminFetchInvoices.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.invoice.onFetchInvoices] error' })

    yield put(AdminFetchInvoices.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load invoices'))
    }
  }
}

export function* onFetchInvoiceDueInterest(
  action: AsyncAction<FetchInvoiceDueInterestActionPayload>,
) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchInvoiceDueInterest.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // The body is `null` when Oatfi reports nothing due. It is forwarded as-is; the card
    // renders an empty state rather than a fabricated $0.00.
    const { payload }: { payload: InvoiceDueInterest | null } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInvoiceDueInterest,
      {
        method: 'GET',
        params: [{ field: ':partnerId', value: partnerId }],
      },
    )

    yield put(AdminFetchInvoiceDueInterest.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.invoice.onFetchInvoiceDueInterest] error' })

    yield put(AdminFetchInvoiceDueInterest.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load due interest'))
    }
  }
}

/**
 * The batch engine. There is no composed-invoice endpoint in para-node, so a composed-level
 * action is N per-invoice POSTs run here. Five properties this shape guarantees:
 *
 * 1. Sequential — these are money movements against Oatfi, never fanned out in parallel.
 * 2. A partial failure RESOLVES (`action.next(null, results)`). `useAsyncDispatchWithErrorToast`
 *    therefore shows no toast, and the modal is the single place the outcome is reported. A red
 *    "failed" toast over a run where five of six succeeded would be wrong.
 * 3. Progress lives in the store — a fresh copy of `results` is put each iteration, so the
 *    reducer never holds a reference this loop keeps mutating.
 * 4. Per-invoice failures skip Sentry. They are the backend's documented business rules firing
 *    as designed ('Invoice is not funded', 'Invoice cannot be canceled'); that is information
 *    for the admin, not an exception. Only a run-level failure is captured.
 * 5. A single-row action is the same code path with `invoiceIds.length === 1` — there is no
 *    second implementation to drift.
 */
export function* onRunInvoiceAction(action: AsyncAction<RunInvoiceActionActionPayload>) {
  const partnerId = action.payload?.partnerId
  const invoiceIds = action.payload?.invoiceIds ?? []
  const invoiceLabels = action.payload?.invoiceLabels ?? {}

  if (!partnerId) {
    yield put(AdminRunInvoiceAction.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  if (invoiceIds.length === 0) {
    yield put(AdminRunInvoiceAction.failed(new Error('No invoices to act on')))

    if (action.next) {
      action.next('No invoices to act on')
    }

    return
  }

  const { verb } = action.payload
  const endpoint = INVOICE_ACTION_ENDPOINTS[verb]
  const fallbackMessage = invoiceActionFallbackMessages[verb]
  const results: InvoiceActionResult[] = []

  try {
    for (let index = 0; index < invoiceIds.length; index++) {
      const invoiceId = invoiceIds[index]
      const label = invoiceLabels[invoiceId] ?? invoiceId

      try {
        const response: CallApiResponse | undefined = yield call(callSecureApi, endpoint, {
          method: 'POST',
          params: [
            { field: ':partnerId', value: partnerId },
            { field: ':invoiceId', value: invoiceId },
          ],
        })

        // `callSecureApi` resolves to `undefined` (without throwing) when a 401 refresh fails and
        // the session is torn down. Treating "did not throw" as success would write `ok: true`
        // for an invoice the backend never accepted — the one thing this results list must never
        // do.
        if (!response || !response.ok) {
          results.push({ invoiceId, label, ok: false, message: fallbackMessage })
        } else {
          results.push({ invoiceId, label, ok: true })
        }
      } catch (error: unknown) {
        results.push({
          invoiceId,
          label,
          ok: false,
          message: resolveErrorMessage(error, fallbackMessage),
        })
      }

      yield put(AdminSetInvoiceActionProgress({ completed: index + 1, results: [...results] }))
    }

    yield put(AdminRunInvoiceAction.success(results))

    if (action.next) {
      action.next(null, results)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.invoice.onRunInvoiceAction] error' })

    yield put(AdminRunInvoiceAction.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to run invoice action'))
    }
  }
}

export function* invoiceSaga() {
  // A page change or refresh re-triggers these, so only the newest run matters.
  yield takeLatest(AdminFetchInvoices.type.REQUEST, onFetchInvoices)
  yield takeLatest(AdminFetchInvoiceDueInterest.type.REQUEST, onFetchInvoiceDueInterest)
  // A money action must never be cancelled by a later one.
  yield takeEvery(AdminRunInvoiceAction.type.REQUEST, onRunInvoiceAction)
}
